// Trophies: worldwide records, personal bests, persistent milestones.
//
// Awards are computed on this device (seconds saved vs the warehouse target),
// so this module is the only thing that can see a ROLLING WINDOW of them.
// Every award lands in a per-flow buffer; each landing recomputes the rolling
// sums for the record windows and, when a number the server told us about is
// beaten, claims it. The server keeps the crowns and settles races — a claim
// that lost to a bigger one elsewhere simply comes back `record: false`.
//
// Milestones split by who can see the crossing:
//   - day / lifetime / golden-hour rungs cross HERE, mid-shift, so this module
//     claims them the moment they do (and celebrates).
//   - streak rungs need the whole points history plus the bank-holiday
//     calendar, so the SERVER grants those quietly during GET /points/trophies
//     and they surface as "new" in the modal instead.
import { apiFetch } from "../../api/apiFetch";
import { getSessionScore } from "../helpers/sessionScore";

export type TrophyFlow =
    | "scan_sheet" | "bf_scan" | "book_in" | "put_away"
    | "sorter_1" | "sorter_2" | "pick_and_sort"
    | "picking_light" | "picking_pick_and_sort" | "picking_large"
    | "drop_shelf" | "drop_forecast"
    | "dispatch_light" | "dispatch_pick_and_sort" | "dispatch_large"
    | "wipe";

export const TROPHY_FLOWS: TrophyFlow[] = [
    "sorter_1", "sorter_2", "pick_and_sort", "scan_sheet", "bf_scan",
    "book_in", "put_away", "picking_light", "picking_pick_and_sort",
    "picking_large", "drop_shelf", "drop_forecast", "dispatch_light",
    "dispatch_pick_and_sort", "dispatch_large", "wipe",
];

/** Parent department → child flows, in floor order. Pickers and boards group
 *  by these so "Picking Single" reads as Single UNDER Picking. */
export const TROPHY_DEPARTMENTS: { key: string; flows: TrophyFlow[] }[] = [
    { key: "sorting", flows: ["sorter_1", "sorter_2", "pick_and_sort"] },
    { key: "goods_in", flows: ["scan_sheet", "bf_scan", "book_in"] },
    { key: "picking", flows: ["picking_light", "picking_pick_and_sort", "picking_large"] },
    { key: "drops", flows: ["drop_shelf", "drop_forecast"] },
    { key: "dispatch", flows: ["dispatch_light", "dispatch_pick_and_sort", "dispatch_large"] },
    { key: "put_away", flows: ["put_away"] },
    { key: "wipe", flows: ["wipe"] },
];

/** Short child label key for use UNDER a department caption — "Single", not
 *  "Picking Single". Flows whose full name is already short keep it. */
export const FLOW_SHORT_KEY: Record<TrophyFlow, string> = {
    scan_sheet: "trophies_flow_scan_sheet",
    bf_scan: "trophies_flow_bf_scan",
    book_in: "trophies_flow_book_in",
    put_away: "trophies_flow_put_away",
    sorter_1: "trophies_flow_sorter_1",
    sorter_2: "trophies_flow_sorter_2",
    pick_and_sort: "trophies_flow_pick_and_sort",
    picking_light: "trophies_sub_single",
    picking_pick_and_sort: "trophies_sub_pas",
    picking_large: "trophies_sub_large",
    drop_shelf: "trophies_sub_shelf",
    drop_forecast: "trophies_sub_forecast",
    dispatch_light: "trophies_sub_single",
    dispatch_pick_and_sort: "trophies_sub_pas",
    dispatch_large: "trophies_sub_large",
    wipe: "trophies_flow_wipe",
};

/** The settings column each cycle-scored flow reads — lets useCycleScore
 *  derive the flow without every screen naming it twice. */
export const TARGET_KEY_TO_FLOW: Record<string, TrophyFlow> = {
    picking_timer_light_ms: "picking_light",
    picking_timer_pick_and_sort_ms: "picking_pick_and_sort",
    picking_timer_large_ms: "picking_large",
    drop_timer_shelf_ms: "drop_shelf",
    drop_timer_forecast_ms: "drop_forecast",
    dispatch_timer_light_ms: "dispatch_light",
    dispatch_timer_pick_and_sort_ms: "dispatch_pick_and_sort",
    dispatch_timer_large_ms: "dispatch_large",
    wipe_timer_ms: "wipe",
};

// 4 hours is the longest window — 8h was retired 2026-08-27 (a whole-shift
// record is really just "best day", which the milestones already cover).
export const RECORD_WINDOWS_MIN = [5, 15, 30, 60, 120, 240] as const;

// Ladders. Full lists live here; the modal only ever SHOWS achieved + the
// next three (see nextThreeRule) so the mountain never stares anyone down.
export const LIFETIME_LADDER = [
    10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 750_000,
    1_000_000, 1_500_000, 2_500_000, 5_000_000, 10_000_000,
];
export const DAY_LADDER = [1_000, 2_500, 5_000, 7_500, 10_000, 12_500, 15_000, 20_000];
export const GOLDEN_HOUR_LADDER = [750, 1_000, 1_500];
export const STREAK_LADDER = [3, 5, 10, 25, 50, 100];

export const TROPHY_OPEN_EVENT = "trophy-modal-open";
export const TROPHY_CELEBRATION_EVENT = "trophy-celebration";
export const TROPHY_BADGE_EVENT = "trophy-badge-updated";

export interface TrophyRecordRow {
    windowMinutes: number;
    uid: number;
    name: string | null;
    mine: boolean;
    points: number;
    items: number;
    warehouse: string | null;
    setAt: string;
}

export interface TrophySnapshot {
    lifetime: number;
    bestDay: number;
    streakDays: number;
    flow: TrophyFlow | null;
    recordsHeld: number;
    unseenMilestones: number;
    milestones: { code: string; value: number; achievedAt: string; seen: boolean }[];
    records: TrophyRecordRow[];
    personalBests: { windowMinutes: number; points: number; items: number; setAt: string }[];
    dethroned: { flow: string; windowMinutes: number; points: number; prevPoints: number; byName: string | null }[];
}

export type TrophyCelebration =
    | {
        kind: "record";
        flow: TrophyFlow;
        windowMinutes: number;
        points: number;
        items: number;
        previous: { name: string | null; points: number; items: number; heldDays: number } | null;
        /** Smaller windows broken in the same burst — one popup covers all. */
        alsoWindows?: number[];
    }
    | { kind: "milestone"; code: string; value: number };

export interface TrophyBadge {
    recordsHeld: number;
    unseen: number;
}

const dispatchEvent = (name: string, detail: unknown) => {
    try {
        window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch {
        // noop
    }
};

const currentUid = (): number | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const uid = Number(JSON.parse(raw)?.uid);
        return Number.isFinite(uid) && uid > 0 ? uid : null;
    } catch {
        return null;
    }
};

// ── badge (pill crown + unseen dot) ─────────────────────────────────────────

let badge: TrophyBadge = { recordsHeld: 0, unseen: 0 };

export const getTrophyBadge = (): TrophyBadge => badge;

const setBadge = (next: TrophyBadge) => {
    if (next.recordsHeld === badge.recordsHeld && next.unseen === badge.unseen) return;
    badge = next;
    dispatchEvent(TROPHY_BADGE_EVENT, badge);
};

// ── API ──────────────────────────────────────────────────────────────────────

export const fetchTrophies = async (flow: TrophyFlow | null): Promise<TrophySnapshot | null> => {
    try {
        const qs = flow ? `?flow=${flow}` : "";
        const res = await apiFetch(`/points/trophies${qs}`, { method: "GET" });
        if (!res.ok) return null;
        const data = (await res.json()) as TrophySnapshot;
        absorbSnapshot(data);
        return data;
    } catch {
        return null;
    }
};

export const markTrophiesSeen = async (): Promise<void> => {
    try {
        await apiFetch(`/points/trophies/seen`, { method: "POST" });
        setBadge({ recordsHeld: badge.recordsHeld, unseen: 0 });
    } catch {
        // next open retries
    }
};

// ── rolling-window tracker ───────────────────────────────────────────────────

interface AwardEvent { t: number; pts: number; items: number }

const MAX_WINDOW_MS = 240 * 60_000;
// One claim attempt per (flow, window) per minute. Early on — before any real
// records exist — every scan "beats the record"; this keeps the bootstrap
// phase from turning into a POST per scan without delaying a real beat by
// more than it takes to prove it.
const CLAIM_INTERVAL_MS = 60_000;

const buffers = new Map<TrophyFlow, AwardEvent[]>();
// Best ITEM COUNT we know per `${flow}:${window}` — max of the worldwide
// record and our own PB. Records rank on items processed, not points: items
// are the fairer metric and they survive target retunes, where a points
// record set under an old target would be forever easier or harder to beat.
// Absent means "snapshot not loaded, don't claim yet".
const bestKnown = new Map<string, number>();
const snapshotLoaded = new Set<TrophyFlow>();
const snapshotRequested = new Set<TrophyFlow>();
const lastClaimAt = new Map<string, number>();
const claimInFlight = new Set<string>();

// Milestone rungs already achieved (from snapshots + this session's claims).
const achieved = new Set<string>();
// Lifetime baseline: server lifetime at snapshot time minus what today's
// total was then — adding the CURRENT today total gives a live lifetime.
let lifetimeBase: number | null = null;
let prevDayTotal = getSessionScore();

const absorbSnapshot = (snap: TrophySnapshot) => {
    if (snap.flow) {
        snapshotLoaded.add(snap.flow);
        for (const w of RECORD_WINDOWS_MIN) {
            const rec = snap.records.find(r => r.windowMinutes === w)?.items ?? 0;
            const pb = snap.personalBests.find(b => b.windowMinutes === w)?.items ?? 0;
            bestKnown.set(`${snap.flow}:${w}`, Math.max(rec, pb));
        }
    }
    for (const m of snap.milestones) achieved.add(m.code);
    lifetimeBase = snap.lifetime - getSessionScore();
    // Re-baseline the day tracker: anything banked before this snapshot is
    // history (already granted server-side), not a live crossing.
    prevDayTotal = getSessionScore();
    setBadge({
        recordsHeld: snap.recordsHeld,
        unseen: snap.unseenMilestones + snap.dethroned.length,
    });
};

const rollingSums = (events: AwardEvent[], now: number, windowMs: number) => {
    let pts = 0;
    let items = 0;
    for (let i = events.length - 1; i >= 0; i--) {
        if (now - events[i].t > windowMs) break;
        pts += events[i].pts;
        items += events[i].items;
    }
    return { pts, items };
};

// ── record celebration batching ─────────────────────────────────────────────
// A first-ever run in a flow beats EVERY empty window at once (5m..8h all
// fall to the same scans), which would fire a popup per window. Wins landing
// within this span merge into ONE celebration, headlined by the biggest
// window with the rest listed beneath it.
export const CELEBRATION_BATCH_MS = 1500;

interface RecordWin {
    windowMinutes: number;
    points: number;
    items: number;
    previous: { name: string | null; points: number; items: number; heldDays: number } | null;
}

let recordBatchFlow: TrophyFlow | null = null;
let recordBatchWins: RecordWin[] = [];
let recordBatchTimer: ReturnType<typeof setTimeout> | null = null;

const flushRecordCelebration = () => {
    if (recordBatchTimer) {
        clearTimeout(recordBatchTimer);
        recordBatchTimer = null;
    }
    if (recordBatchFlow === null || recordBatchWins.length === 0) {
        recordBatchFlow = null;
        recordBatchWins = [];
        return;
    }
    const [head, ...rest] = [...recordBatchWins].sort((a, b) => b.windowMinutes - a.windowMinutes);
    const celebration: TrophyCelebration = {
        kind: "record",
        flow: recordBatchFlow,
        windowMinutes: head.windowMinutes,
        points: head.points,
        items: head.items,
        previous: head.previous,
        alsoWindows: rest.map(w => w.windowMinutes),
    };
    recordBatchFlow = null;
    recordBatchWins = [];
    dispatchEvent(TROPHY_CELEBRATION_EVENT, celebration);
};

const queueRecordCelebration = (flow: TrophyFlow, win: RecordWin) => {
    // A win in a different flow ends the previous batch immediately.
    if (recordBatchFlow !== null && recordBatchFlow !== flow) flushRecordCelebration();
    recordBatchFlow = flow;
    recordBatchWins.push(win);
    if (recordBatchTimer) clearTimeout(recordBatchTimer);
    recordBatchTimer = setTimeout(flushRecordCelebration, CELEBRATION_BATCH_MS);
};

const claimRecord = async (flow: TrophyFlow, windowMinutes: number, points: number, items: number) => {
    const key = `${flow}:${windowMinutes}`;
    if (claimInFlight.has(key)) return;
    claimInFlight.add(key);
    try {
        const res = await apiFetch(`/points/records`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ flow, windowMinutes, points, items }),
        });
        if (!res.ok) return;
        const data = await res.json();
        // Whatever the outcome, this item count is now the floor to beat.
        bestKnown.set(key, Math.max(bestKnown.get(key) ?? 0, items));
        if (data?.record === true) {
            const me = currentUid();
            const ownBeat =
                data.previous != null && me !== null && Number(data.previous.uid) === me;
            if (!ownBeat) {
                // A crown you already wore doesn't add to the count — and
                // improving your own record celebrates silently, or the
                // bootstrap phase would fire a popup every claim interval as
                // people beat the record they set a minute earlier.
                setBadge({ recordsHeld: badge.recordsHeld + 1, unseen: badge.unseen });
                queueRecordCelebration(flow, {
                    windowMinutes,
                    points,
                    items,
                    previous: data.previous ?? null,
                });
            }
        }
    } catch {
        // lost claims re-fire on the next beat
    } finally {
        claimInFlight.delete(key);
    }
};

const claimMilestone = async (code: string, value: number) => {
    if (achieved.has(code)) return;
    achieved.add(code); // optimistic — ON CONFLICT makes a wrong guess harmless
    try {
        const res = await apiFetch(`/points/milestones`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.achieved === true) {
            const celebration: TrophyCelebration = { kind: "milestone", code, value };
            dispatchEvent(TROPHY_CELEBRATION_EVENT, celebration);
        }
    } catch {
        achieved.delete(code); // let a later crossing retry
    }
};

/**
 * One award landed: `seconds` saved over `items` units of work in `flow`.
 * Called from addSessionSeconds so every scoring screen feeds the tracker
 * through the one door it already uses.
 */
export const recordAward = (flow: TrophyFlow, seconds: number, items = 1) => {
    noteFirstScanOfDay();
    const now = Date.now();
    const buf = buffers.get(flow) ?? [];
    buf.push({ t: now, pts: seconds, items });
    while (buf.length > 0 && now - buf[0].t > MAX_WINDOW_MS) buf.shift();
    buffers.set(flow, buf);

    // First award in a flow: learn the numbers to beat before claiming.
    if (!snapshotLoaded.has(flow) && !snapshotRequested.has(flow)) {
        snapshotRequested.add(flow);
        void fetchTrophies(flow);
    }

    if (snapshotLoaded.has(flow)) {
        for (const w of RECORD_WINDOWS_MIN) {
            const key = `${flow}:${w}`;
            const { pts, items: windowItems } = rollingSums(buf, now, w * 60_000);
            const best = bestKnown.get(key) ?? 0;
            if (windowItems <= best) continue;
            const last = lastClaimAt.get(key) ?? 0;
            if (now - last < CLAIM_INTERVAL_MS) continue;
            lastClaimAt.set(key, now);
            void claimRecord(flow, w, Math.max(1, pts), windowItems);
        }

        // Golden hour rides the same buffer.
        const hour = rollingSums(buf, now, 60 * 60_000).pts;
        for (const rung of GOLDEN_HOUR_LADDER) {
            if (hour >= rung) void claimMilestone(`golden_hour_${rung}`, rung);
        }
    }

    // Day + lifetime rungs cross on the running daily total — but only once
    // a snapshot has loaded. Before it, the achieved set is empty and the
    // baseline is whatever localStorage held at module load, so the very
    // first award of a session could "cross" rungs a veteran passed months
    // ago and pop a celebration the server would have suppressed a second
    // later. History belongs to the snapshot; only live crossings celebrate.
    const dayTotal = getSessionScore();
    if (lifetimeBase !== null) {
        for (const rung of DAY_LADDER) {
            if (dayTotal >= rung && prevDayTotal < rung) void claimMilestone(`day_${rung}`, rung);
        }
        const lifetime = lifetimeBase + dayTotal;
        for (const rung of LIFETIME_LADDER) {
            if (lifetime >= rung && lifetime - (dayTotal - prevDayTotal) < rung) {
                void claimMilestone(`lifetime_${rung}`, rung);
            }
        }
    }
    prevDayTotal = dayTotal;
};

// ── trophy gate ──────────────────────────────────────────────────────────────
// Looking at trophies is a break, and breaks are rationed: the modal can be
// OPENED manually only once per interval (a warehouse setting, default 2h),
// with the clock anchored on the first scoring scan of the day and re-anchored
// every time the modal opens — including celebration auto-opens. Resets daily.

const TROPHY_GATE_KEY = "trophy-gate";

interface GateState { uid: number; date: string; anchorAt: number }

const gateTodayKey = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const readGate = (): GateState | null => {
    try {
        const raw = window.localStorage.getItem(TROPHY_GATE_KEY);
        if (!raw) return null;
        const g = JSON.parse(raw) as GateState;
        const uid = currentUid();
        if (uid === null || g.uid !== uid || g.date !== gateTodayKey()) return null;
        return typeof g.anchorAt === "number" ? g : null;
    } catch {
        return null;
    }
};

const writeGate = (anchorAt: number) => {
    const uid = currentUid();
    if (uid === null) return;
    try {
        const state: GateState = { uid, date: gateTodayKey(), anchorAt };
        window.localStorage.setItem(TROPHY_GATE_KEY, JSON.stringify(state));
    } catch {
        // localStorage unavailable — the gate fails open via readGate() null
    }
};

/** First scoring scan of the day arms the gate; later scans leave it alone. */
export const noteFirstScanOfDay = () => {
    if (readGate() !== null) return;
    writeGate(Date.now());
};

/** Every modal open — manual or celebration — restarts the interval. */
export const noteTrophyOpen = () => {
    writeGate(Date.now());
};

/**
 * Milliseconds until the modal may next be opened manually.
 * 0 = eligible now. Infinity = no scoring scan yet today (work first).
 */
export const trophyOpenEligibleInMs = (intervalMinutes: number): number => {
    const g = readGate();
    if (g === null) return Infinity;
    return Math.max(0, g.anchorAt + intervalMinutes * 60_000 - Date.now());
};

// ── display helpers ──────────────────────────────────────────────────────────

/**
 * The anti-demoralisation rule: achieved rungs, then the next three, then a
 * "+N more" count for what stays out of sight.
 */
export const nextThreeRule = (ladder: number[], reached: number) => {
    const achievedRungs = ladder.filter(v => reached >= v);
    const upcoming = ladder.filter(v => reached < v);
    return {
        achieved: achievedRungs,
        next: upcoming.slice(0, 3),
        hidden: Math.max(0, upcoming.length - 3),
    };
};

/** Whole days since an ISO timestamp — "held 34d". */
export const daysSince = (iso: string): number => {
    const ms = Date.now() - new Date(iso).getTime();
    return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 86_400_000)) : 0;
};

export const formatPoints = (n: number): string => n.toLocaleString("en-GB");

/** Test seam: wipe module state between unit tests. */
export const __resetTrophyState = () => {
    if (recordBatchTimer) clearTimeout(recordBatchTimer);
    recordBatchTimer = null;
    recordBatchFlow = null;
    recordBatchWins = [];
    buffers.clear();
    bestKnown.clear();
    snapshotLoaded.clear();
    snapshotRequested.clear();
    lastClaimAt.clear();
    claimInFlight.clear();
    achieved.clear();
    lifetimeBase = null;
    prevDayTotal = getSessionScore();
    badge = { recordsHeld: 0, unseen: 0 };
};

/** Test seam: mark a flow's snapshot as known without a network round trip. */
export const __primeFlow = (flow: TrophyFlow, best: Record<number, number>, lifetime = 0) => {
    snapshotLoaded.add(flow);
    snapshotRequested.add(flow);
    for (const w of RECORD_WINDOWS_MIN) bestKnown.set(`${flow}:${w}`, best[w] ?? 0);
    lifetimeBase = lifetime - getSessionScore();
};
